import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  StatusIndicatorProps,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";

import { RootState } from "../../app/rootReducer";
import { formatUtcDate } from "./commentFormatting";
import {
  fetchProgramTickets,
  setProgramTicketsPage,
  setProgramTicketsSelection,
} from "./programTicketsConnector";
import {
  ProgramTicketsTableFilter,
  ProgramTicketsTableHeader,
} from "./ProgramTicketsHeader";
import { rollupStatusOf } from "./rollupStatus";
import { ProgramTicket, RollupStatus } from "./types";

export const ROLLUP_TO_INDICATOR: Record<
  RollupStatus,
  StatusIndicatorProps.Type
> = {
  open: "warning",
  "work-in-progress": "in-progress",
  resolved: "success",
};

export const StatusIndicatorCell: React.FC<{ row: ProgramTicket }> = ({
  row,
}) => {
  const rollup = rollupStatusOf(row.status);
  return (
    <StatusIndicator type={ROLLUP_TO_INDICATOR[rollup]}>
      <span aria-label={row.status}>{row.status}</span>
    </StatusIndicator>
  );
};

// Initial widths are required for `resizableColumns` to render sensibly
// on first paint — without them Cloudscape falls back to auto-layout and
// the first drag causes a large jump. Users can resize from these seeds.
export const COLUMN_DEFINITIONS: readonly TableProps.ColumnDefinition<ProgramTicket>[] =
  [
    {
      id: "program",
      header: "Program",
      cell: (row) => row.programName,
      width: 160,
      minWidth: 100,
    },
    {
      id: "ticket",
      header: "Ticket",
      cell: (row) => (
        <Link
          external
          externalIconAriaLabel={`Opens ${row.ticketId} in SIM-T`}
          href={row.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.ticketId}
        </Link>
      ),
      width: 140,
      minWidth: 100,
    },
    {
      id: "title",
      header: "Title",
      cell: (row) => row.title,
      width: 420,
      minWidth: 200,
    },
    {
      id: "createdAt",
      header: "Created",
      cell: (row) => formatUtcDate(row.createdAt),
      width: 120,
      minWidth: 100,
    },
    {
      id: "lastUpdatedAt",
      header: "Last Updated",
      cell: (row) => formatUtcDate(row.lastUpdatedAt),
      width: 140,
      minWidth: 110,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => <StatusIndicatorCell row={row} />,
      width: 170,
      minWidth: 130,
    },
  ];

export const ARIA_LABELS: TableProps.AriaLabels<ProgramTicket> = {
  selectionGroupLabel: "ticket selection",
  itemSelectionLabel: (_data, item) => `Select ticket ${item.ticketId}`,
};

const ZERO_STATE_MESSAGE = (
  <Box>
    {
      "Apply a filter to view program tickets. For example, `Program: Madeleine`."
    }
  </Box>
);

const NO_TICKETS_MESSAGE = (
  <Box>No program tickets found for the current filter.</Box>
);

function computeNextSelection(
  prevSelectedIds: readonly string[],
  prevFocusedId: string | undefined,
  nextSelectedItems: readonly ProgramTicket[]
): {
  readonly selectedTicketIds: readonly string[];
  readonly focusedTicketId: string | undefined;
} {
  const nextIds = nextSelectedItems.map((row) => row.ticketId);
  const nextIdSet = new Set(nextIds);
  const added = nextIds.filter((id) => !prevSelectedIds.includes(id));

  if (added.length > 0) {
    return {
      selectedTicketIds: nextIds,
      focusedTicketId: added[added.length - 1],
    };
  }

  if (prevFocusedId !== undefined && nextIdSet.has(prevFocusedId)) {
    return {
      selectedTicketIds: nextIds,
      focusedTicketId: prevFocusedId,
    };
  }

  if (nextIds.length === 0) {
    return { selectedTicketIds: [], focusedTicketId: undefined };
  }

  for (let i = prevSelectedIds.length - 1; i >= 0; i--) {
    const candidate = prevSelectedIds[i];
    if (nextIdSet.has(candidate)) {
      return {
        selectedTicketIds: nextIds,
        focusedTicketId: candidate,
      };
    }
  }

  return {
    selectedTicketIds: nextIds,
    focusedTicketId: nextIds[nextIds.length - 1],
  };
}

export const ProgramTicketsTable: React.FC = () => {
  const dispatch = useDispatch<any>();

  const list = useSelector((state: RootState) => state.programTickets.list);
  const totalCount = useSelector(
    (state: RootState) => state.programTickets.totalCount
  );
  const page = useSelector((state: RootState) => state.programTickets.page);
  const pageSize = useSelector(
    (state: RootState) => state.programTickets.pageSize
  );
  const status = useSelector((state: RootState) => state.programTickets.status);
  const error = useSelector((state: RootState) => state.programTickets.error);
  const selectedTicketIds = useSelector(
    (state: RootState) => state.programTickets.selectedTicketIds
  );
  const focusedTicketId = useSelector(
    (state: RootState) => state.programTickets.focusedTicketId
  );
  const lastFetchedKey = useSelector(
    (state: RootState) => state.programTickets.lastFetchedKey
  );
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );

  // `ValidationDashboardPage` renders `<NoFiltersPanel/>` in place of
  // the tab tree when queryString is empty or `(all)`, so this
  // component never mounts in that state. The only zero-state we
  // still handle here is when the `program` aggregation returned no
  // program buckets — a filter is applied but no ASINs carry a
  // program value that produced any concept IDs.
  const showFilterPrompt =
    status === "success" && list.length === 0 && lastFetchedKey === undefined;
  const showErrorAlert = status === "error";
  const showLoadingTable = status === "loading" && list.length === 0;
  const showNoTicketsMessage =
    !showFilterPrompt &&
    !showErrorAlert &&
    !showLoadingTable &&
    status === "success" &&
    list.length === 0 &&
    lastFetchedKey !== undefined;

  const selectedItems = useMemo<readonly ProgramTicket[]>(
    () => list.filter((row) => selectedTicketIds.includes(row.ticketId)),
    [list, selectedTicketIds]
  );

  const pagesCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPageIndex = page + 1;

  const handleSelectionChange = useCallback(
    (event: {
      readonly detail: { readonly selectedItems: readonly ProgramTicket[] };
    }) => {
      const next = computeNextSelection(
        selectedTicketIds,
        focusedTicketId,
        event.detail.selectedItems
      );
      dispatch(setProgramTicketsSelection(next));
    },
    [dispatch, selectedTicketIds, focusedTicketId]
  );

  const handleRetry = useCallback(() => {
    dispatch(
      fetchProgramTickets({
        searchQueryString: queryString,
        page,
      })
    );
  }, [dispatch, queryString, page]);

  const handlePageChange = useCallback(
    (event: { readonly detail: { readonly currentPageIndex: number } }) => {
      const nextZeroIndexed = event.detail.currentPageIndex - 1;
      dispatch(setProgramTicketsPage(nextZeroIndexed));
      dispatch(
        fetchProgramTickets({
          searchQueryString: queryString,
          page: nextZeroIndexed,
        })
      );
    },
    [dispatch, queryString]
  );

  if (showFilterPrompt) {
    return ZERO_STATE_MESSAGE;
  }

  if (showErrorAlert) {
    return (
      <Alert type="error" header="Could not load program tickets">
        <SpaceBetween size="s">
          {error !== undefined ? <Box variant="p">{error}</Box> : null}
          <Button onClick={handleRetry}>Retry</Button>
        </SpaceBetween>
      </Alert>
    );
  }

  return (
    <Table
      header={<ProgramTicketsTableHeader />}
      filter={<ProgramTicketsTableFilter />}
      columnDefinitions={
        COLUMN_DEFINITIONS as TableProps.ColumnDefinition<ProgramTicket>[]
      }
      items={list as ProgramTicket[]}
      loading={showLoadingTable}
      loadingText="Loading program tickets..."
      selectionType="multi"
      selectedItems={selectedItems as ProgramTicket[]}
      onSelectionChange={handleSelectionChange}
      ariaLabels={ARIA_LABELS}
      trackBy="ticketId"
      resizableColumns
      stickyHeader
      empty={showNoTicketsMessage ? NO_TICKETS_MESSAGE : undefined}
      pagination={
        <Pagination
          pagesCount={pagesCount}
          currentPageIndex={currentPageIndex}
          onChange={handlePageChange}
        />
      }
    />
  );
};

export default ProgramTicketsTable;
